<script setup lang="ts">
import type { DeepReadonly } from "vue";
import { computed } from "vue";

import type { SightlineTextBlock } from "@/backend/natsstubs/sightline/models";

const props = defineProps<{ block: DeepReadonly<SightlineTextBlock>; error?: boolean }>();

// Deliberately restricted Markdown: paragraphs, `-`/`*`/`1.` lists, fenced
// blocks kept literal, and **bold**. Everything is rendered through
// interpolation — never swap in a Markdown library with v-html, the text is
// model output.
interface Section {
  tag: "p" | "ul" | "ol";
  lines: string[];
  start?: number;
  literal?: boolean;
}

const sections = computed(() => {
  const result: Section[] = [];
  let current: Section | undefined;
  let fence: string | undefined;
  for (const line of props.block.text.split(/\r?\n/)) {
    if (fence) {
      current!.lines.push(line);
      if (line.trimEnd() === fence) {
        fence = undefined;
        current = undefined;
      }
      continue;
    }
    const openingFence = /^(`{3,}|~{3,})/.exec(line);
    if (openingFence) {
      fence = openingFence[1];
      current = { tag: "p", lines: [line], literal: true };
      result.push(current);
      continue;
    }
    if (!line.trim()) {
      current = undefined;
      continue;
    }
    const bullet = /^[ \t]*[-*][ \t]+(.+)$/.exec(line);
    const numbered = /^[ \t]*(\d+)\.[ \t]+(.+)$/.exec(line);
    const tag = bullet ? "ul" : numbered ? "ol" : "p";
    if (current?.tag !== tag) {
      current = { tag, lines: [], start: numbered ? Number(numbered[1]) : undefined };
      result.push(current);
    }
    current.lines.push(bullet?.[1] ?? numbered?.[2] ?? line);
  }
  return result;
});

function inline(text: string) {
  return text.split(/((?<!\*)\*\*[^*]+\*\*(?!\*))/g).map((part, index) => {
    const strong = index % 2 === 1;
    return { strong, text: strong ? part.slice(2, -2) : part };
  });
}
</script>

<template>
  <div class="sightline-text-block" :class="{ error }">
    <component
      :is="section.tag"
      v-for="(section, index) in sections"
      :key="index"
      class="prose-section"
      :start="section.start"
    >
      <template v-if="section.literal">{{ section.lines.join("\n") }}</template>
      <template v-else-if="section.tag === 'p'">
        <template v-for="(part, partIndex) in inline(section.lines.join('\n'))" :key="partIndex">
          <strong v-if="part.strong">{{ part.text }}</strong>
          <template v-else>{{ part.text }}</template>
        </template>
      </template>
      <template v-else>
        <li v-for="(line, lineIndex) in section.lines" :key="lineIndex">
          <template v-for="(part, partIndex) in inline(line)" :key="partIndex">
            <strong v-if="part.strong">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </li>
      </template>
    </component>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.sightline-text-block {
  @include typography.medium("regular");

  width: 100%;
  margin: 0;
  color: colors.$text;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: break-word;

  &.error {
    color: colors.$text-subtle;
  }

  .prose-section {
    margin: 0;
  }

  .prose-section + .prose-section {
    margin-top: 14px;
  }

  li + li {
    margin-top: 6px;
  }

  ul,
  ol {
    padding-left: 24px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  strong {
    @include typography.medium("semi-bold");
  }
}
</style>
